import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { User, UserCreate, UserLogin } from "@/types";
import {
  getCurrentUser,
  loginUser,
  logoutUser,
  registerUser,
  ssoLoginUser,
  withDraw,
} from "@/services/auth-service";
import { mockAuthService } from "@/services/mock-sso-service";

const IS_MOCK_SSO = import.meta.env.VITE_MOCK_SSO === 'true';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: UserLogin) => Promise<void>;
  register: (payload: UserCreate) => Promise<void>;
  logout: () => void;
  handleSsoCallback: (ssoAccessToken: string, ssoRefreshToken: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  withdraw: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }
    getCurrentUser()
      .then(setUser)
      .catch(() => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Listen for SPA-friendly logout triggered by axios interceptor on 401.
  // We clear state here so ProtectedRoute redirects via <Navigate>, avoiding
  // a hard navigation that would fire beforeunload on /chat and trigger the
  // browser's "Leave site?" popup.
  useEffect(() => {
    const handleAuthLogout = () => {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    };
    window.addEventListener("auth:logout", handleAuthLogout);
    return () => window.removeEventListener("auth:logout", handleAuthLogout);
  }, []);

  const storeTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  };

  const login = useCallback(async (credentials: UserLogin) => {
    const tokens = await loginUser(credentials);
    storeTokens(tokens.access_token, tokens.refresh_token);
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  }, []);

  const register = useCallback(async (payload: UserCreate) => {
    await registerUser(payload);
    // Registration no longer auto-logs in — user must wait for admin approval then SSO login
  }, []);

  const handleSsoCallback = useCallback(async (ssoAccessToken: string, _ssoRefreshToken: string) => {
    let tokens;
    let currentUser;
    if (IS_MOCK_SSO) {
      tokens = await mockAuthService.ssoLoginUser(ssoAccessToken);
      storeTokens(tokens.access_token, tokens.refresh_token);
      currentUser = await mockAuthService.getCurrentUser();
    } else {
      tokens = await ssoLoginUser(ssoAccessToken);
      storeTokens(tokens.access_token, tokens.refresh_token);
      currentUser = await getCurrentUser();
    }
    setUser(currentUser);
  }, []);

  const logout = useCallback(() => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      logoutUser(refreshToken).catch(() => {});
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
    navigate("/");
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  }, []);

  const withdraw = useCallback(async () => {
    await withDraw();
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
    navigate("/");
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, isAuthenticated: !!user, login, register, logout, handleSsoCallback, refreshUser, withdraw }),
    [user, isLoading, login, register, logout, handleSsoCallback, refreshUser, withdraw],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
